import { Link, Navigate } from "react-router-dom";
import { ArrowRight, BellRing, CircleCheckBig, MailWarning, Rocket } from "lucide-react";

import type { Movimiento } from "../api/tipos.ts";
import { usarCorreosSinLeer, usarLibro } from "../ganchos/datos.ts";
import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { PERMISO } from "../sesion/permisos.ts";
import { fechaCorta, pesos, plural, soloHora } from "../formato.ts";
import { ESTADO_MOVIMIENTO } from "../vocabulario.ts";
import { ErrorConsulta } from "../componentes/Errores.tsx";
import { Aviso, Boton, Esqueleto, Insignia, Tarjeta, Vacio, cx } from "../componentes/ui.tsx";
import { usarPasosDeInicio } from "./Empezar.tsx";
import { cifrasDeHoy } from "./resumen-modelo.ts";
import { enOrdenDeBandeja } from "../componentes/atajo-de-pago.ts";

/**
 * Mientras falte algún paso del alta, el dueño lo ve aquí, que es donde entra
 * (SPEC-029 §15). Sale solo cuando están los cinco.
 */
function FaltaEmpezar() {
  const { cuantos, listo } = usarPasosDeInicio();
  if (!listo || cuantos >= 5) return null;
  return (
    <Aviso tono="dato" titulo={`Te ${plural(5 - cuantos, "falta", "faltan")} ${5 - cuantos} ${plural(5 - cuantos, "paso", "pasos")} para que tus pagos lleguen solos`} icono={Rocket}>
      <Link to="/empezar" className="font-medium underline">
        Terminar de configurar 4drando
      </Link>
    </Aviso>
  );
}

/**
 * Correos del banco que parecen pagos y no se pudieron leer (SPEC-031).
 *
 * Va ARRIBA DE TODO y en rojo, antes que las cifras: si el banco cambió su
 * redacción, las cifras de abajo están incompletas y no lo saben. El 19-oct-2026
 * Lorus Vanessa llevaba 39 días con 16 pagos sin cargar, y Lorus 71 en 9, sin
 * que ninguna pantalla lo dijera.
 */
function CorreosSinLeer() {
  const { data } = usarCorreosSinLeer();
  if (!data || data.n === 0) return null;
  const { n, vacios } = data;
  return (
    <Aviso
      tono="peligro"
      titulo={`${n} ${plural(n, "correo", "correos")} de tu banco no se ${plural(n, "pudo", "pudieron")} leer`}
      icono={MailWarning}
    >
      {plural(n, "Parece un pago recibido", "Parecen pagos recibidos")} y{" "}
      <strong>{plural(n, "no está cargado", "no están cargados")}</strong>.
      {vacios > 0 &&
        ` ${vacios === n ? plural(n, "Llegó", "Llegaron") : `${vacios} ${plural(vacios, "llegó", "llegaron")}`} en blanco.`} Avísale a quien
      administra 4drando.{" "}
      <Link to="/recibidos" className="font-medium underline">
        Ver los correos
      </Link>
    </Aviso>
  );
}

/**
 * La primera pantalla responde a una sola pregunta: **¿cómo va hoy y qué me
 * toca?** (SPEC-029 §16).
 *
 * Tres cifras y lo más urgente de la bandeja. Lo demás que vivía aquí —el
 * cuadre del mes, la rosca de cuánto se resolvió solo— es interesante pero no
 * es una tarea, y está en Métricas.
 */
export function Resumen() {
  const { data: libro, isPending, error, refetch } = usarLibro();
  const { cuenta, cuentaNombre, rol, puedo } = usarSesion();

  // El cajero empieza en la caja (SPEC-029 §12): su pregunta es «¿ya llegó?»,
  // y el resumen responde otras.
  if (rol === "cajero") return <Navigate to="/caja" replace />;
  if (error) return <ErrorConsulta error={error} alReintentar={() => void refetch()} />;

  const verCartera = puedo(PERMISO.VER_CARTERA);
  const c = libro ? cifrasDeHoy(libro, verCartera) : null;
  const alertas = libro?.alertas.length ?? 0;

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          {cuentaNombre || cuenta ? `Pagos de ${cuentaNombre || cuenta}` : "Tus pagos"}
        </h1>
        <p className="text-sm text-tenue first-letter:uppercase">
          {new Date().toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long" })}
        </p>
      </header>

      <CorreosSinLeer />

      {puedo(PERMISO.CONFIGURAR) && <FaltaEmpezar />}

      {!c ? (
        isPending && <EsqueletoResumen />
      ) : (
        <>
          <div className={cx("grid gap-4", c.faltaCobrar !== null ? "sm:grid-cols-3" : "sm:grid-cols-2")}>
            <Cifra
              rotulo="Entró hoy"
              valor={pesos(c.entro.centavos)}
              pie={c.entro.pagos ? `${c.entro.pagos} ${plural(c.entro.pagos, "pago", "pagos")}` : "Todavía nada"}
              a="/movimientos"
            />
            <Cifra
              rotulo="Falta decir de quién es"
              valor={pesos(c.faltaDecir.centavos)}
              pie={
                c.faltaDecir.pagos
                  ? `${c.faltaDecir.pagos} ${plural(c.faltaDecir.pagos, "pago", "pagos")}${c.faltaDecir.deHoy ? ` · ${c.faltaDecir.deHoy} de hoy` : ""}`
                  : "Nada: todo tiene dueño"
              }
              a="/bandeja"
              tono={c.faltaDecir.pagos ? "aviso" : undefined}
            />
            {c.faltaCobrar !== null && (
              <Cifra rotulo="Falta cobrar" valor={pesos(c.faltaCobrar)} pie="Saldo de tus facturas" a="/cartera" />
            )}
          </div>

          {alertas > 0 && (
            <Link
              to="/alertas"
              className="flex items-center gap-2 rounded-xl border border-borde bg-superficie px-4 py-3 text-sm text-tenue hover:border-borde-fuerte hover:text-tinta"
            >
              <BellRing className="size-4 shrink-0 text-riesgo" aria-hidden />
              <span className="flex-1">
                <span className="font-medium text-tinta">
                  {alertas} {plural(alertas, "pago registrado", "pagos registrados")}
                </span>{" "}
                todavía no {plural(alertas, "aparece", "aparecen")} en el banco.
              </span>
              <ArrowRight className="size-4" aria-hidden />
            </Link>
          )}

          <Urgentes pendientes={enOrdenDeBandeja(libro?.pendientes ?? [])} />
        </>
      )}
    </div>
  );
}

function Cifra({
  rotulo,
  valor,
  pie,
  a,
  tono,
}: {
  rotulo: string;
  valor: string;
  pie: string;
  a: string;
  tono?: "aviso";
}) {
  return (
    <Link to={a} className="group">
      <Tarjeta className="h-full space-y-1 p-5 transition-colors group-hover:border-borde-fuerte">
        <p className="text-sm text-tenue">{rotulo}</p>
        <p className="cifra-titular text-2xl sm:text-3xl">{valor}</p>
        <p className={cx("text-xs", tono === "aviso" ? "font-medium text-aviso-tinta" : "text-tenue-2")}>{pie}</p>
      </Tarjeta>
    </Link>
  );
}

/** Los primeros de la bandeja, con lo justo para reconocerlos. */
function Urgentes({ pendientes }: { pendientes: Movimiento[] }) {
  return (
    <Tarjeta className="flex flex-col">
      <div className="flex items-baseline justify-between gap-4 border-b border-borde px-5 py-4">
        <h2 className="font-semibold tracking-tight">Lo más urgente</h2>
        {pendientes.length > 0 && (
          <Link to="/bandeja" className="text-sm font-medium text-marca hover:underline">
            Ver los {pendientes.length}
          </Link>
        )}
      </div>

      {pendientes.length === 0 ? (
        <Vacio icono={CircleCheckBig} titulo="Nada pendiente">
          Cuando entre un pago que 4drando no pueda explicar, aparecerá aquí.
        </Vacio>
      ) : (
        <>
          <ul className="divide-y divide-borde">
            {pendientes.slice(0, 5).map((m) => {
              const hora = soloHora(m.fecha);
              return (
                <li key={m.clave}>
                  <Link
                    to={`/bandeja?pago=${encodeURIComponent(m.clave)}`}
                    className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 hover:bg-superficie-2/50"
                  >
                    <span className="w-36 shrink-0 text-xs whitespace-nowrap text-tenue-2">
                      {fechaCorta(m.fecha)}
                      {hora ? ` · ${hora}` : ""}
                    </span>
                    <span className="cifra w-32 shrink-0 font-semibold text-tinta">{pesos(m.montoCentavos)}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-tinta">
                      {m.contraparte || "el banco no dice quién"}
                    </span>
                    <Insignia tono={ESTADO_MOVIMIENTO[m.estado].tono}>{ESTADO_MOVIMIENTO[m.estado].texto}</Insignia>
                  </Link>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-borde p-4">
            <Link to="/bandeja">
              <Boton variante="primario" bloque icono={ArrowRight}>
                Ir a la bandeja
              </Boton>
            </Link>
          </div>
        </>
      )}
    </Tarjeta>
  );
}

const EsqueletoResumen = () => (
  <div className="space-y-6">
    <div className="grid gap-4 sm:grid-cols-3">
      {Array.from({ length: 3 }, (_, i) => (
        <Tarjeta key={i} className="space-y-3 p-5">
          <Esqueleto className="h-4 w-28" />
          <Esqueleto className="h-8 w-36" />
          <Esqueleto className="h-3 w-20" />
        </Tarjeta>
      ))}
    </div>
    <Tarjeta className="p-6">
      <Esqueleto className="h-40 w-full" />
    </Tarjeta>
  </div>
);
